import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { TrendingUp, Activity, X, Clock, Lightbulb, Calendar, MousePointerClick } from 'lucide-react';

const C = { w: 800, h: 470, pt: 48, pr: 28, pb: 72, pl: 66 };
const A = {
  l: C.pl, r: C.w - C.pr, t: C.pt, b: C.h - C.pb,
  get w() { return this.r - this.l; },
  get h() { return this.b - this.t; },
};

const frontData = [
  { m: 0, d: 0 }, { m: 0.5, d: 8 }, { m: 1, d: 5 }, { m: 1.5, d: 2 },
  { m: 2, d: 3 }, { m: 3, d: 10 }, { m: 4, d: 22 }, { m: 5, d: 38 },
  { m: 6, d: 52 }, { m: 7, d: 64 }, { m: 8, d: 74 }, { m: 9, d: 82 },
  { m: 10, d: 88 }, { m: 11, d: 92 }, { m: 12, d: 95 }, { m: 15, d: 97 }, { m: 18, d: 98 },
];

const crownData = [
  { m: 0, d: 0 }, { m: 0.5, d: 8 }, { m: 1, d: 5 }, { m: 1.5, d: 2 },
  { m: 2, d: 3 }, { m: 3, d: 6 }, { m: 4, d: 14 }, { m: 5, d: 24 },
  { m: 6, d: 35 }, { m: 7, d: 44 }, { m: 8, d: 53 }, { m: 9, d: 61 },
  { m: 10, d: 68 }, { m: 11, d: 74 }, { m: 12, d: 80 }, { m: 15, d: 91 }, { m: 18, d: 97 },
];

const phases = [
  { s: 0, e: 1, key: 'healing', color: '#3b82f6' },
  { s: 1, e: 2.5, key: 'shockloss', color: '#f97316' },
  { s: 2.5, e: 4, key: 'resting', color: '#eab308' },
  { s: 4, e: 10, key: 'growth', color: '#22c55e' },
  { s: 10, e: 18, key: 'maturation', color: '#7BA7C2' },
];

const phaseI18n = {
  healing: { de: 'Heilung', en: 'Healing' },
  shockloss: { de: 'Shock Loss', en: 'Shock Loss' },
  resting: { de: 'Ruhephase', en: 'Rest' },
  growth: { de: 'Wachstum', en: 'Growth' },
  maturation: { de: 'Reifung', en: 'Maturation' },
};

const phaseModalData = {
  healing: {
    image: 'https://images.unsplash.com/photo-1665791566713-d67ce4ed7cac?w=600&h=360&fit=crop&auto=format',
    time: { de: 'Tag 1 – Monat 1', en: 'Day 1 – Month 1' },
    density: '0–8%',
    title: { de: 'Heilungsphase', en: 'Healing Phase' },
    desc: {
      de: 'Direkt nach der DHI-Transplantation beginnt die Heilung. In den ersten Tagen bilden sich kleine Krusten um die implantierten Grafts, die innerhalb von 7–10 Tagen natürlich abfallen. Leichte Rötungen und Schwellungen im Empfängerbereich sind völlig normal und klingen innerhalb von 2 Wochen ab. Der Spenderbereich ist bei der DHI-Methode durch Ihr bestehendes Haar verborgen — maximale Diskretion von Tag 1.',
      en: 'Immediately after DHI transplantation, healing begins. Small crusts form around the implanted grafts in the first few days, naturally falling off within 7–10 days. Slight redness and swelling in the recipient area are completely normal and subside within 2 weeks. With the DHI method, the donor area is hidden by your existing hair — maximum discretion from day 1.',
    },
    tips: {
      de: ['Kopf beim Schlafen erhöht lagern', 'Ab Tag 10 vorsichtig mit speziellem Shampoo waschen', 'Direkte Sonneneinstrahlung vermeiden'],
      en: ['Keep head elevated while sleeping', 'Gently wash with special shampoo from day 10', 'Avoid direct sun exposure'],
    },
  },
  shockloss: {
    image: 'https://images.unsplash.com/photo-1535100699743-cea5b806bfaf?w=600&h=360&fit=crop&auto=format',
    time: { de: 'Monat 1 – 2,5', en: 'Month 1 – 2.5' },
    density: '2–5%',
    title: { de: 'Shock Loss Phase', en: 'Shock Loss Phase' },
    desc: {
      de: 'Ein vorübergehender, aber erwarteter Schritt: Die transplantierten Haare fallen aus. Das klingt beunruhigend, ist aber ein natürlicher Teil des Prozesses. Die Haarwurzeln bleiben sicher in der Kopfhaut verankert und treten in einen neuen Wachstumszyklus ein. Die Dichte sinkt temporär auf ihren niedrigsten Punkt — Geduld zahlt sich hier aus.',
      en: 'A temporary but expected step: transplanted hairs fall out. This sounds alarming but is a natural part of the process. Hair roots remain safely anchored in the scalp and enter a new growth cycle. Density temporarily drops to its lowest point — patience pays off here.',
    },
    tips: {
      de: ['Kein Grund zur Sorge — völlig normal', 'Nicht an den Haaren ziehen oder kratzen', 'Stressreduktion unterstützt den Heilungsprozess'],
      en: ['No reason to worry — completely normal', 'Do not pull or scratch the hair', 'Stress reduction supports the healing process'],
    },
  },
  resting: {
    image: 'https://images.unsplash.com/photo-1518281361980-b26bfd556770?w=600&h=360&fit=crop&auto=format',
    time: { de: 'Monat 2,5 – 4', en: 'Month 2.5 – 4' },
    density: '3–15%',
    title: { de: 'Ruhephase (Telogen)', en: 'Resting Phase (Telogen)' },
    desc: {
      de: 'Die Follikel befinden sich in der Telogenphase — einer natürlichen Ruhephase. Äußerlich ist wenig zu sehen, doch unter der Haut passiert Entscheidendes: Die Haarwurzeln regenerieren sich, bilden neue Zellstrukturen und bereiten das kräftige Wachstum vor. Diese Phase ist der Grundstein für das spätere Ergebnis.',
      en: 'Follicles are in the telogen phase — a natural resting period. Little is visible externally, but beneath the skin crucial processes are underway: hair roots regenerate, form new cell structures, and prepare for strong growth. This phase is the foundation for the final result.',
    },
    tips: {
      de: ['Geduld bewahren — die Wurzeln arbeiten unsichtbar', 'Gesunde Ernährung mit Biotin und Zink fördert Wachstum', 'Regelmäßige Kontrolltermine wahrnehmen'],
      en: ['Stay patient — roots are working beneath the surface', 'Healthy nutrition with biotin and zinc supports growth', 'Attend regular follow-up appointments'],
    },
  },
  growth: {
    image: 'https://images.unsplash.com/photo-1760425701471-d90374f6114d?w=600&h=360&fit=crop&auto=format',
    time: { de: 'Monat 4 – 10', en: 'Month 4 – 10' },
    density: '15–88%',
    title: { de: 'Aktive Wachstumsphase', en: 'Active Growth Phase' },
    desc: {
      de: 'Jetzt wird es spannend! Die neuen Haare sprießen mit zunehmender Geschwindigkeit. Anfangs sind sie fein und hell, werden aber Monat für Monat kräftiger und dicker. Ab Monat 6 sind deutliche Verbesserungen sichtbar — die Haare können normal gewaschen und gestylt werden. Der Frontalbereich zeigt bereits beeindruckende Ergebnisse, während der Tonsurbereich etwas länger braucht.',
      en: 'Now it gets exciting! New hairs sprout at increasing speed. Initially fine and light, they become stronger and thicker month by month. From month 6, significant improvements are visible — hair can be washed and styled normally. The frontal area already shows impressive results, while the crown area takes slightly longer.',
    },
    tips: {
      de: ['Ergebnisse dokumentieren mit monatlichen Fotos', 'Sanftes Styling ohne aggressive Hitze erlaubt', 'PRP-Therapie kann Wachstum zusätzlich fördern'],
      en: ['Document results with monthly photos', 'Gentle styling without aggressive heat is allowed', 'PRP therapy can further promote growth'],
    },
  },
  maturation: {
    image: 'https://images.unsplash.com/photo-1664051830150-28a69a2072d1?w=600&h=360&fit=crop&auto=format',
    time: { de: 'Monat 10 – 18', en: 'Month 10 – 18' },
    density: '88–98%',
    title: { de: 'Reifung & Endergebnis', en: 'Maturation & Final Result' },
    desc: {
      de: 'Die letzte Phase bringt das finale Ergebnis. Die Haare haben ihre volle Dicke, natürliche Textur und endgültige Farbe erreicht. Der Frontalbereich (Geheimratsecken, Haarlinie) ist bereits ab Monat 12 komplett. Der Tonsurbereich (Hinterkopf/Wirbel) braucht bis zu 18 Monate für die maximale Dichte. Das Endergebnis ist dauerhaft — die transplantierten Haare wachsen ein Leben lang.',
      en: 'The final phase delivers the ultimate result. Hair has reached full thickness, natural texture, and final color. The frontal area (temples, hairline) is complete by month 12. The crown area (back/vertex) takes up to 18 months for maximum density. The result is permanent — transplanted hair grows for a lifetime.',
    },
    tips: {
      de: ['Endergebnis mit Vorher-Fotos vergleichen', 'Normale Haarpflege-Routine möglich', 'Jährliche Kontrolle empfohlen'],
      en: ['Compare final result with before photos', 'Normal hair care routine is possible', 'Annual check-up recommended'],
    },
  },
};

const toXY = (month, density) => ({
  x: A.l + (month / 18) * A.w,
  y: A.b - (density / 100) * A.h,
});

const makePath = (data) => {
  const pts = data.map(p => toXY(p.m, p.d));
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const t = 6;
    d += ` C ${p1.x + (p2.x - p0.x) / t} ${p1.y + (p2.y - p0.y) / t}, ${p2.x - (p3.x - p1.x) / t} ${p2.y - (p3.y - p1.y) / t}, ${p2.x} ${p2.y}`;
  }
  return d;
};

const makeArea = (data) => {
  const line = makePath(data);
  const last = toXY(data[data.length - 1].m, data[data.length - 1].d);
  const first = toXY(data[0].m, data[0].d);
  return `${line} L ${last.x} ${A.b} L ${first.x} ${A.b} Z`;
};

const lerp = (data, month) => {
  if (month <= data[0].m) return data[0].d;
  if (month >= data[data.length - 1].m) return data[data.length - 1].d;
  for (let i = 0; i < data.length - 1; i++) {
    if (month >= data[i].m && month <= data[i + 1].m) {
      const t = (month - data[i].m) / (data[i + 1].m - data[i].m);
      return data[i].d + t * (data[i + 1].d - data[i].d);
    }
  }
  return 0;
};

const FRONT_CLR = '#7BA7C2';
const CROWN_CLR = '#22d3ee';
const PATH_LEN = 2200;
const Y_TICKS = [0, 25, 50, 75, 100];
const X_TICKS = [0, 3, 6, 9, 12, 15, 18];

const HairGrowthTimeline = () => {
  const { features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null);
  const [visible, setVisible] = useState(false);
  const [selPhase, setSelPhase] = useState(null);
  const [modalPhase, setModalPhase] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hasClickedChip, setHasClickedChip] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!modalPhase) return;
    const onKey = (e) => { if (e.key === 'Escape') setModalPhase(null); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [modalPhase]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const onMove = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const sx = C.w / rect.width;
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const svgX = cx * sx;
    if (svgX >= A.l && svgX <= A.r) {
      setHover(Math.round(((svgX - A.l) / A.w) * 18 * 2) / 2);
    } else {
      setHover(null);
    }
  }, []);

  const fLine = useMemo(() => makePath(frontData), []);
  const cLine = useMemo(() => makePath(crownData), []);
  const fArea = useMemo(() => makeArea(frontData), []);
  const cArea = useMemo(() => makeArea(crownData), []);

  if (!features?.showHairGrowthTimeline) return null;

  const t = (de, en) => language === 'de' ? de : en;
  const hf = hover !== null ? lerp(frontData, hover) : null;
  const hc = hover !== null ? lerp(crownData, hover) : null;
  const ap = hover !== null
    ? phases.find(p => hover >= p.s && hover < p.e) || phases[phases.length - 1]
    : null;
  const sp = selPhase ? phases.find(p => p.key === selPhase) : null;
  const mp = modalPhase ? phaseModalData[modalPhase] : null;
  const mpColor = modalPhase ? (phases.find(p => p.key === modalPhase)?.color || '#7BA7C2') : null;

  return (
    <section
      ref={wrapRef}
      data-testid="hair-growth-timeline"
      className={`py-16 sm:py-24 relative overflow-hidden transition-colors duration-300 ${
        isDark ? 'bg-[#060a13]' : 'bg-[#f0f4f8]'
      }`}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20'
          }`}>
            <Activity className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
            <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
              {t('Wachstumsprognose', 'Growth Forecast')}
            </span>
          </div>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('Haarwuchs-', 'Hair Growth ')}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#22d3ee] bg-clip-text text-transparent">
              {t('Verlauf', 'Trend')}
            </span>
          </h2>
          <p className={`max-w-xl mx-auto text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t(
              'Haarwuchsdichte (%) im Zeitverlauf nach DHI-Transplantation',
              'Hair density (%) over time after DHI transplantation'
            )}
          </p>
        </div>

        {/* Chart Card */}
        <div className={`rounded-2xl border overflow-hidden ${
          isDark ? 'bg-[#0d1117] border-white/[0.08]' : 'bg-white border-gray-200 shadow-xl'
        }`}>
          {/* Ticker bar */}
          <div className={`flex items-center justify-between px-4 sm:px-6 py-3 border-b ${
            isDark ? 'border-white/[0.06] bg-[#161b22]' : 'border-gray-100 bg-gray-50/80'
          }`}>
            <div className="flex items-center gap-2.5">
              <TrendingUp className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
              <span className={`text-sm font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                DHI {t('Wachstumskurve', 'Growth Curve')}
              </span>
              {hover !== null && (
                <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                  isDark ? 'bg-white/[0.06] text-emerald-400' : 'bg-emerald-50 text-emerald-700'
                }`}>
                  +{Math.round(hf)}%
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-[2px] rounded-full" style={{ background: FRONT_CLR }} />
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{t('Frontal', 'Front')}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-[2px] rounded-full" style={{ background: CROWN_CLR }} />
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{t('Tonsur', 'Crown')}</span>
              </span>
            </div>
          </div>

          {/* SVG */}
          <div className="relative" data-testid="growth-chart">
            <svg
              ref={svgRef}
              viewBox={`0 0 ${C.w} ${C.h}`}
              className="w-full h-auto cursor-crosshair select-none"
              onMouseMove={onMove}
              onTouchMove={onMove}
              onMouseLeave={() => setHover(null)}
              onTouchEnd={() => setHover(null)}
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <linearGradient id="fGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={FRONT_CLR} stopOpacity={isDark ? 0.28 : 0.22} />
                  <stop offset="100%" stopColor={FRONT_CLR} stopOpacity="0.01" />
                </linearGradient>
                <linearGradient id="cGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CROWN_CLR} stopOpacity={isDark ? 0.18 : 0.14} />
                  <stop offset="100%" stopColor={CROWN_CLR} stopOpacity="0.01" />
                </linearGradient>
                <filter id="gl">
                  <feGaussianBlur stdDeviation="2.5" result="b" />
                  <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>

              {/* Phase zones */}
              {phases.map(p => {
                const x1 = A.l + (p.s / 18) * A.w;
                const x2 = A.l + (p.e / 18) * A.w;
                const isHighlighted = selPhase === p.key;
                return (
                  <rect key={p.key} x={x1} y={A.t} width={x2 - x1} height={A.h}
                    fill={p.color} opacity={isHighlighted ? (isDark ? 0.12 : 0.14) : (isDark ? 0.035 : 0.05)} 
                    style={{ transition: 'opacity 0.3s' }} />
                );
              })}

              {/* Horizontal grid + Y labels */}
              {(isMobile ? [0, 50, 100] : Y_TICKS).map(v => {
                const y = A.b - (v / 100) * A.h;
                return (
                  <g key={`y${v}`}>
                    <line x1={A.l} y1={y} x2={A.r} y2={y}
                      stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)'}
                      strokeDasharray={v === 0 ? '' : '3 4'} />
                    <text x={A.l - 10} y={y + 5} textAnchor="end"
                      fill={isDark ? '#6b7280' : '#9ca3af'} fontSize="15"
                      fontFamily="ui-monospace, SFMono-Regular, monospace">{v}%</text>
                  </g>
                );
              })}

              {/* Vertical grid + X labels */}
              {(isMobile ? [0, 6, 12, 18] : X_TICKS).map(mo => {
                const x = A.l + (mo / 18) * A.w;
                return (
                  <g key={`x${mo}`}>
                    <line x1={x} y1={A.t} x2={x} y2={A.b}
                      stroke={isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.05)'}
                      strokeDasharray="3 4" />
                    <text x={x} y={A.b + 24} textAnchor="middle"
                      fill={isDark ? '#6b7280' : '#9ca3af'} fontSize="15"
                      fontFamily="ui-monospace, SFMono-Regular, monospace">
                      {mo === 0 ? 'Start' : `${mo}M`}
                    </text>
                  </g>
                );
              })}

              {/* Phase labels top — hidden on mobile, shown in chip bar */}
              {!isMobile && phases.map(p => {
                const mx = A.l + ((p.s + p.e) / 2 / 18) * A.w;
                return (
                  <text key={`pl-${p.key}`} x={mx} y={A.t - 10} textAnchor="middle"
                    fill={p.color} fontSize="12" fontWeight="600"
                    fontFamily="ui-monospace, SFMono-Regular, monospace"
                    opacity={selPhase === p.key ? 1 : 0.7}
                    style={{ transition: 'opacity 0.3s' }}>
                    {phaseI18n[p.key][language === 'de' ? 'de' : 'en']}
                  </text>
                );
              })}

              {/* Area fills */}
              <path d={cArea} fill="url(#cGrad)"
                opacity={visible ? 1 : 0} style={{ transition: 'opacity 0.8s ease-out 0.6s' }} />
              <path d={fArea} fill="url(#fGrad)"
                opacity={visible ? 1 : 0} style={{ transition: 'opacity 0.8s ease-out 0.3s' }} />

              {/* Crown line */}
              <path d={cLine} fill="none" stroke={CROWN_CLR} strokeWidth="2"
                strokeLinecap="round" opacity="0.65"
                strokeDasharray={PATH_LEN}
                strokeDashoffset={visible ? 0 : PATH_LEN}
                style={{ transition: `stroke-dashoffset 2.2s ease-out 0.5s` }} />

              {/* Front line (primary) */}
              <path d={fLine} fill="none" stroke={FRONT_CLR} strokeWidth="2.5"
                strokeLinecap="round" filter="url(#gl)"
                strokeDasharray={PATH_LEN}
                strokeDashoffset={visible ? 0 : PATH_LEN}
                style={{ transition: `stroke-dashoffset 2s ease-out 0.3s` }} />

              {/* Key data point dots (front line) */}
              {visible && [0, 3, 6, 9, 12, 18].map(mo => {
                const pt = frontData.find(p => p.m === mo);
                if (!pt) return null;
                const { x, y } = toXY(pt.m, pt.d);
                return (
                  <circle key={`fd${mo}`} cx={x} cy={y} r="3.5"
                    fill={FRONT_CLR} stroke={isDark ? '#0d1117' : '#fff'} strokeWidth="1.5"
                    opacity={visible ? 1 : 0}
                    style={{ transition: `opacity 0.4s ease-out ${0.3 + mo * 0.08}s` }} />
                );
              })}

              {/* Hover crosshair */}
              {hover !== null && (
                <>
                  <line x1={toXY(hover, 0).x} y1={A.t} x2={toXY(hover, 0).x} y2={A.b}
                    stroke={isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)'}
                    strokeDasharray="3 3" />
                  <line x1={A.l} y1={toXY(0, hf).y} x2={A.r} y2={toXY(0, hf).y}
                    stroke={FRONT_CLR} strokeDasharray="2 3" opacity="0.3" />
                  <circle cx={toXY(hover, hf).x} cy={toXY(hover, hf).y} r="6"
                    fill={FRONT_CLR} stroke={isDark ? '#0d1117' : '#fff'} strokeWidth="2.5" />
                  <circle cx={toXY(hover, hc).x} cy={toXY(hover, hc).y} r="5"
                    fill={CROWN_CLR} stroke={isDark ? '#0d1117' : '#fff'} strokeWidth="2" />
                </>
              )}
            </svg>

            {/* Hover Tooltip */}
            {hover !== null && (
              <div
                data-testid="growth-tooltip"
                className={`absolute top-3 right-3 sm:top-4 sm:right-4 px-3.5 py-2.5 rounded-xl text-xs backdrop-blur-md ${
                  isDark
                    ? 'bg-[#161b22]/90 border border-white/10 text-white'
                    : 'bg-white/90 border border-gray-200 shadow-lg text-gray-900'
                }`}
              >
                <div className={`mb-1.5 font-semibold font-mono ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  {t('Monat', 'Month')} {hover}
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: FRONT_CLR }} />
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{t('Frontal', 'Front')}</span>
                  <span className="font-bold font-mono ml-auto" style={{ color: FRONT_CLR }}>{Math.round(hf)}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: CROWN_CLR }} />
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{t('Tonsur', 'Crown')}</span>
                  <span className="font-bold font-mono ml-auto" style={{ color: CROWN_CLR }}>{Math.round(hc)}%</span>
                </div>
                {ap && (
                  <div className={`mt-1.5 pt-1.5 border-t text-[10px] ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                    <span className="font-semibold" style={{ color: ap.color }}>
                      {phaseI18n[ap.key][language === 'de' ? 'de' : 'en']}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Phase chips + animated cursor indicator */}
          <div className={`relative px-4 sm:px-6 py-3 border-t ${
            isDark ? 'border-white/[0.06] bg-[#161b22]' : 'border-gray-100 bg-gray-50/80'
          }`}>
            <div className="flex flex-wrap items-center gap-2">
              {phases.map(p => (
                <button
                  key={p.key}
                  data-testid={`phase-chip-${p.key}`}
                  className={`px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium transition-all duration-200 border cursor-pointer ${
                    selPhase === p.key ? 'scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    borderColor: p.color + (selPhase === p.key ? '80' : '30'),
                    background: p.color + (selPhase === p.key ? '25' : '10'),
                    color: p.color,
                    opacity: selPhase && selPhase !== p.key ? 0.5 : 1,
                  }}
                  onClick={() => {
                    setSelPhase(selPhase === p.key ? null : p.key);
                    setModalPhase(p.key);
                    setHasClickedChip(true);
                  }}
                >
                  {phaseI18n[p.key][language === 'de' ? 'de' : 'en']}
                </button>
              ))}
            </div>

            {/* Animated cursor moving to Heilung chip */}
            {!hasClickedChip && visible && (
              <div
                className="pointer-events-none absolute z-20"
                style={{ top: '4px', left: '28px', animation: 'cursorMove 3s ease-in-out 1s infinite' }}
              >
                <MousePointerClick
                  className={`w-10 h-10 sm:w-11 sm:h-11 ${isDark ? 'text-white' : 'text-gray-700'}`}
                  style={{ animation: 'cursorTap 3s ease-in-out 1s infinite', filter: isDark ? 'drop-shadow(0 0 8px rgba(255,255,255,0.4))' : 'drop-shadow(0 2px 8px rgba(0,0,0,0.25))' }}
                />
              </div>
            )}
          </div>

          {/* CSS animations for cursor */}
          <style>{`
            @keyframes cursorMove {
              0% { transform: translate(50px, -30px); opacity: 0; }
              10% { opacity: 1; }
              35% { transform: translate(0px, 0px); opacity: 1; }
              45% { transform: translate(0px, 0px); opacity: 1; }
              50% { transform: translate(0px, 0px); opacity: 1; }
              60% { transform: translate(0px, 0px); opacity: 0.9; }
              70% { transform: translate(50px, -30px); opacity: 0; }
              100% { transform: translate(50px, -30px); opacity: 0; }
            }
            @keyframes cursorTap {
              0%, 34% { transform: scale(1) rotate(0deg); }
              38% { transform: scale(0.75) rotate(-5deg); }
              44% { transform: scale(1) rotate(0deg); }
              100% { transform: scale(1) rotate(0deg); }
            }
          `}</style>
        </div>

        {/* Bottom legend */}
        <div className={`mt-5 flex flex-col sm:flex-row justify-between gap-2 text-[11px] ${
          isDark ? 'text-gray-500' : 'text-gray-400'
        }`}>
          <span className="flex items-center gap-1.5">
            <span className="w-6 h-[2px] rounded-full" style={{ background: FRONT_CLR }} />
            {t('Frontalbereich — Endergebnis ca. 12 Monate', 'Front area — Final result ~12 months')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-6 h-[2px] rounded-full" style={{ background: CROWN_CLR }} />
            {t('Tonsurbereich — Endergebnis ca. 18 Monate', 'Crown area — Final result ~18 months')}
          </span>
        </div>
      </div>

      {/* Phase Detail Modal */}
      {mp && (
        <div
          data-testid="phase-modal-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) setModalPhase(null); }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setModalPhase(null)} />

          {/* Modal Card */}
          <div
            data-testid="phase-modal"
            className={`relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border shadow-2xl animate-in zoom-in-95 fade-in duration-200 ${
              isDark ? 'bg-[#0d1117] border-white/10' : 'bg-white border-gray-200'
            }`}
          >
            {/* Image */}
            <div className="relative h-44 sm:h-52 overflow-hidden rounded-t-2xl">
              <img
                src={mp.image}
                alt={mp.title[language === 'de' ? 'de' : 'en']}
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              {/* Phase badge on image */}
              <div className="absolute bottom-3 left-4 flex items-center gap-2">
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold text-white"
                  style={{ background: mpColor + 'cc' }}
                >
                  {phaseI18n[modalPhase][language === 'de' ? 'de' : 'en']}
                </span>
                <span className="text-white/80 text-xs font-mono">
                  {mp.density}
                </span>
              </div>
              {/* Close button */}
              <button
                data-testid="phase-modal-close"
                onClick={() => setModalPhase(null)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white hover:bg-black/60 transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 sm:p-6">
              {/* Title + Time */}
              <h3 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {mp.title[language === 'de' ? 'de' : 'en']}
              </h3>
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-3.5 h-3.5" style={{ color: mpColor }} />
                <span className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {mp.time[language === 'de' ? 'de' : 'en']}
                </span>
                <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>|</span>
                <span className={`text-xs font-mono font-semibold`} style={{ color: mpColor }}>
                  {mp.density} {t('Dichte', 'density')}
                </span>
              </div>

              {/* Description */}
              <p className={`text-sm leading-relaxed mb-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {mp.desc[language === 'de' ? 'de' : 'en']}
              </p>

              {/* Tips */}
              <div className={`rounded-xl p-4 ${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-gray-50 border border-gray-100'}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <Lightbulb className="w-4 h-4" style={{ color: mpColor }} />
                  <span className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    {t('Tipps für diese Phase', 'Tips for this phase')}
                  </span>
                </div>
                <ul className="space-y-2">
                  {mp.tips[language === 'de' ? 'de' : 'en'].map((tip, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: mpColor }} />
                      <span className={`text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Navigation between phases */}
              <div className="flex items-center justify-between mt-5 pt-4 border-t" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                {(() => {
                  const idx = phases.findIndex(p => p.key === modalPhase);
                  const prev = idx > 0 ? phases[idx - 1] : null;
                  const next = idx < phases.length - 1 ? phases[idx + 1] : null;
                  return (
                    <>
                      {prev ? (
                        <button
                          data-testid="phase-modal-prev"
                          onClick={() => { setModalPhase(prev.key); setSelPhase(prev.key); }}
                          className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                            isDark ? 'text-gray-400 hover:text-white hover:bg-white/[0.06]' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          &larr; {phaseI18n[prev.key][language === 'de' ? 'de' : 'en']}
                        </button>
                      ) : <span />}
                      {next ? (
                        <button
                          data-testid="phase-modal-next"
                          onClick={() => { setModalPhase(next.key); setSelPhase(next.key); }}
                          className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                            isDark ? 'text-gray-400 hover:text-white hover:bg-white/[0.06]' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          {phaseI18n[next.key][language === 'de' ? 'de' : 'en']} &rarr;
                        </button>
                      ) : <span />}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HairGrowthTimeline;
